import type { JsonSchema } from '@/shared/ui/SchemaForm';

/**
 * Fields the schema form does not render, because the page renders them itself.
 * They are removed from the JSON Schema before it reaches the form.
 */
const CUSTOM_RENDERED_FIELDS: Record<
  string,
  { detect: (fieldSchema: Record<string, unknown>) => boolean }
> = {
  templates: {
    detect: (s) => s.type === 'object' && 'additionalProperties' in s,
  },
};

/**
 * Separates the fields the schema form renders from the ones rendered by hand. Module level so
 * the same split feeds both the form and `hasRequiredKnowledgeBase`, which must judge exactly the
 * schema the user was shown.
 */
export function splitCustomFields(jsonSchema: Record<string, unknown>): {
  rjsfSchema: Record<string, unknown>;
  customFields: string[];
} {
  const schema = jsonSchema as JsonSchema;
  const properties = schema.properties;
  const required = schema.required ?? [];
  if (!properties) return { rjsfSchema: jsonSchema, customFields: [] };

  const custom: string[] = [];
  const filtered: Record<string, unknown> = {};

  for (const [key, fieldSchema] of Object.entries(properties)) {
    if (
      key in CUSTOM_RENDERED_FIELDS &&
      typeof fieldSchema === 'object' &&
      CUSTOM_RENDERED_FIELDS[key].detect(fieldSchema as unknown as Record<string, unknown>)
    ) {
      custom.push(key);
    } else {
      filtered[key] = fieldSchema;
    }
  }

  if (custom.length === 0) return { rjsfSchema: jsonSchema, customFields: [] };

  return {
    rjsfSchema: {
      ...jsonSchema,
      properties: filtered,
      required: required.filter((r) => !custom.includes(r)),
    },
    customFields: custom,
  };
}

const KNOWLEDGE_BASE_FIELD = 'knowledgeBaseIds';

interface KnowledgeBaseFieldSchema {
  maxItems?: number;
}

/**
 * Whether the config carries the knowledge bases its tool needs, for the save button and the
 * schedule gate that live outside the form.
 *
 * Deliberately answered by hand rather than by validating the whole schema. Ajv compiles every
 * schema into generated source and evaluates it, which the served Content-Security-Policy
 * forbids: it threw on every call, the answer came back "invalid", and a workspace with a
 * knowledge base was told it had none. Nothing here evaluates a string as code.
 *
 * Entries that are not ids are dropped before counting: a config stored by the form this page
 * used to run on can arrive holding a single `null` that would otherwise pass.
 *
 * A tool that declares no knowledge base field has nothing to satisfy and passes.
 */
export function hasRequiredKnowledgeBase(
  jsonSchema: Record<string, unknown>,
  formData: Record<string, unknown>,
): boolean {
  const properties = (jsonSchema as JsonSchema).properties;
  const fieldSchema = properties?.[KNOWLEDGE_BASE_FIELD] as KnowledgeBaseFieldSchema | undefined;
  if (!fieldSchema) return true;

  const value = formData[KNOWLEDGE_BASE_FIELD];
  const ids = Array.isArray(value) ? value.filter((id) => typeof id === 'string' && id !== '') : [];

  return ids.length >= 1 && ids.length <= (fieldSchema.maxItems ?? Infinity);
}
